import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTenant } from "@/context/tenant-context";
import { useAgentAccess } from "@/hooks/use-agent-access";
import { agentTasksApi, type AgentTask } from "@/api/agent-tasks";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { NewTaskForm } from "@/components/tasks/new-task-form";
import { TaskDetailDrawer } from "@/components/tasks/task-detail-drawer";
import { TaskList } from "@/components/tasks/task-list";
import { TaskSyncNote, useAutoTaskSync } from "@/components/tasks/task-sync";

type Filter = "all" | "recurring" | "one_off";

/** Recurring / One-Off / All filter, shared look with the popup Tasks page. */
export function TaskTypeFilter({
  value,
  onChange,
  tasks,
}: {
  value: Filter;
  onChange: (f: Filter) => void;
  tasks: AgentTask[];
}) {
  return (
    <div className="flex gap-1">
      {(
        [
          ["all", "All"],
          ["recurring", "Recurring"],
          ["one_off", "One-Off"],
        ] as const
      ).map(([key, label]) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          className={cn(
            "rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors",
            value === key ? "bg-accent/10 text-accent" : "text-text-muted hover:text-text-primary",
          )}
        >
          {label}
          <span className="ml-1 opacity-60">
            {key === "all" ? tasks.length : tasks.filter((t) => t.type === key).length}
          </span>
        </button>
      ))}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────
export function AgentTasksTab({ agentId }: { agentId: string }) {
  const { tenantId } = useTenant();
  const { canManage } = useAgentAccess(agentId);
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const adapterType = agents.find((a) => a.id === agentId)?.adapterType;
  const [filter, setFilter] = useState<Filter>("all");
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTask, setSelectedTask] = useState<AgentTask | null>(null);

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: queryKeys.agentTasks.byAgent(agentId),
    queryFn: () => agentTasksApi.list(tenantId, { agentId }),
  });

  // OpenClaw agents only: fetch jobs the agent set up on its own VM.
  const sync = useAutoTaskSync(agentId, adapterType);

  const filtered = filter === "all" ? tasks : tasks.filter((t) => t.type === filter);

  return (
    // Same frame as the Access tab: p-6 inset, a heading row with the actions
    // on the right, then the list.
    <div className="h-full space-y-3 overflow-y-auto p-6">
      <div className="flex items-center gap-3">
        <h3 className="text-sm font-medium text-text-primary">Tasks</h3>
        <TaskTypeFilter value={filter} onChange={setFilter} tasks={tasks} />
        <div className="ml-auto flex items-center gap-2">
          {canManage && (
            <Button size="sm" onClick={() => setShowCreate(!showCreate)}>
              <Plus size={14} className="mr-1" />
              New Task
            </Button>
          )}
        </div>
      </div>
      {(sync.syncing || sync.failed) && (
        <p className="-mt-1 text-xs text-text-muted">
          <TaskSyncNote {...sync} />
        </p>
      )}

      {showCreate &&<NewTaskForm agentId={agentId} onDone={() => setShowCreate(false)} />}

      {isLoading ? (
        <p className="text-sm text-text-muted">Loading...</p>
      ) : (
        <TaskList
          tasks={filtered}
          onOpen={setSelectedTask}
          emptyText={
            filter === "all"
              ? "No tasks yet."
              : `No ${filter === "recurring" ? "recurring" : "one-off"} tasks.`
          }
        />
      )}

      <TaskDetailDrawer task={selectedTask} tenantId={tenantId} onClose={() => setSelectedTask(null)} />
    </div>
  );
}
